import type {
  GetSecurePaymentInfoResponse,
  MerchantFeeConfigResponse,
  MerchantFeeDetailsResponse,
  PayToSupportedBanksResponse,
  SecurePaymentInfo,
  StripeSavedCardsV2Response,
  PaymentScheduleResponse,
  PaymentScheduleResponseData,
  PaymentScheduleDetailsResponse,
  PaymentDetailsForOrdersResponse,
  CreatePaymentRequestResponse,
  PaymentRequest,
  AdjustPaymentScheduleResponse,
  AdjustPaymentScheduleResponseData,
  PayPalSettings,
  PaymentDetails,
  DynamicPaymentScheduleResponse,
  GetOffSessionPaymentMethodByRegionAndPurposeIdResponse,
  LuxPlusSubscriptionRenewalPaymentCards,
} from 'api/payment'
import { arrayToObject } from 'lib/array/arrayUtils'
import { CLASSIC_REWARD_PAYMENT_TYPE } from 'constants/payment'

type PaymentItemBreakdown = NonNullable<PaymentDetailsForOrdersResponse['result'][number]['payments'][number]['items_breakdown']>[number]
type ClassicRewardPointsDetails = Extract<NonNullable<PaymentItemBreakdown['item_le_points_details']>, { reservation_id: string }>

function isClassicRewardPointsDetails(
  details: NonNullable<PaymentItemBreakdown['item_le_points_details']>,
): details is ClassicRewardPointsDetails {
  return 'reservation_id' in details
}

export function merchantFeeConfigMap(result: MerchantFeeConfigResponse['result']): Array<App.MerchantFeeConfig> {
  return result.merchantFeeConfigs.map(merchantFeeConfig => ({
    id: merchantFeeConfig.id,
    name: merchantFeeConfig.name,
    percentage: merchantFeeConfig.percentage,
  }))
}

export function merchantFeeDetailsMap(result: MerchantFeeDetailsResponse['result']): Array<App.MerchantFeeDetails> {
  return result.merchantFeeDetails.filter(item => item.intent === 'sale').map((merchantFeeDetail): App.MerchantFeeDetails => ({
    orderItemId: merchantFeeDetail.fk_item,
    amount: parseFloat(merchantFeeDetail.amount),
    shouldAutoRefund: merchantFeeDetail.metadata.data.shouldAutoRefund,
    merchantFeeId: merchantFeeDetail.id_merchant_fee,
    paymentId: merchantFeeDetail.fk_payment,
    metadata: merchantFeeDetail.metadata,
  }))
}

export function payToSupportedBanksMap(result: PayToSupportedBanksResponse['result']): Array<App.PayToSupportedBank> {
  return result.map(payToSupportedBank => ({
    bankName: payToSupportedBank.bankName,
    bankCode: payToSupportedBank.bankCode,
    bsbPrefixes: payToSupportedBank.bsbPrefixes,
    bankLogo: payToSupportedBank.bankLogo,
    bankAppName: payToSupportedBank.bankAppName,
    bankOnlineBankingName: payToSupportedBank.bankOnlineBankingName,
    faqLink: payToSupportedBank.faqLink,
  }))
}

export function savedStripeCardsMap(result: StripeSavedCardsV2Response['result']): Array<App.StripePaymentCardV2> {
  return result.map(savedCard => ({
    last4: savedCard.last4,
    maskedPan: savedCard.maskedPan,
    expMonth: savedCard.expMonth,
    expYear: savedCard.expYear,
    expiry: savedCard.expiry,
    brand: savedCard.brand,
    logo: savedCard.logo,
    country: savedCard.country,
    paymentMethodId: savedCard.paymentMethodId,
    fingerprint: savedCard.fingerprint,
    funding: savedCard.funding,
    isDefault: savedCard.isDefault,
  }))
}

export function luxPlusOffSessionCardsByRegionAndPurposeIdMap(
  result: GetOffSessionPaymentMethodByRegionAndPurposeIdResponse['result'],
): LuxPlusSubscriptionRenewalPaymentCards {
  return {
    paymentMethod: result.paymentMethod,
    paymentProvider: result.paymentProvider,
    expMonth: result.expMonth,
    expYear: result.expYear,
    promoCode: result.promoCode,
    promoId: result.promoId,
    is100PercentOffPromo: result.is100PercentOffPromo,
    bin: result.bin,
    cards: result.cards.map(card => ({
      id: card.id,
      paymentMethod: card.paymentMethod,
      paymentProvider: card.paymentProvider,
      priority: card.priority,
      expMonth: card.expMonth,
      expYear: card.expYear,
      bin: card.bin,
      card: card.card,
    })),
  }
}

export function securePaymentInfoMap(result: GetSecurePaymentInfoResponse['result']): SecurePaymentInfo {
  return {
    type: result.type,
    amount: result.amount,
    currency: result.currency,
    orderId: result.orderId,
    customerId: result.customerId,
    brand: result.brand,
    isDeposit: result.depositInfo.depositPaymentType === 'initial',
    isDepositBalance: result.depositInfo.depositPaymentType === 'final',
    isPaid: result.isPaid,
    isExpired: result.isExpired,
    hasLuxPlus: result.hasLuxPlus,
    items: result.paymentPlanItems.map(paymentPlanItem => ({
      total: paymentPlanItem.totalAmount,
      type: paymentPlanItem.type,
    })),
    creditAmount: result.creditAmount,
    promoAmount: result.promoAmount,
    balanceAmount: result.depositInfo.balanceAmount,
    balanceDueDate: result.depositInfo.balanceDueDate,
    metadata: result.metadata,
  }
}

export function paymentScheduleDataMap(result: PaymentScheduleResponse['result']): PaymentScheduleResponseData {
  return {
    eligible: result.eligible,
    ineligibilityReason: result.ineligibility_reason,
    selectedTemplateId: result.template_id,
    charges: result.payment_schedules?.map((schedule) => ({
      id: '',
      paymentType: schedule.payment_type,
      dueDate: schedule.due_date,
      baseAmount: schedule.base_amount,
      feeAmount: schedule.fee_amount,
      totalAmount: schedule.total_amount,
      creditApplied: schedule.credit_applied,
      expectedAmount: schedule.expected_amount,
      retry: schedule.retry && { maxChargeAttempts: schedule.retry.max_charge_attempts },
      fee: schedule.fee,
      eligibleItem: schedule.eligible_item && {
        totalAmount: schedule.eligible_item?.total_amount,
      },
      itemsBreakdown: schedule.items_breakdown?.map(item => ({
        itemId: item.id_items,
        totalAmount: item.total_amount,
        type: item.type,
      })),
    })),
  }
}

export function dynamicPaymentScheduleDataMap(result: DynamicPaymentScheduleResponse['result']): App.DynamicPaymentScheduleChargeData {
  return {
    eligible: result.eligible,
    ineligibilityReason: result.ineligibility_reason,
    dynamicPaymentScheduleOptions: result.dynamic_payment_schedule_options?.map(option => ({
      firstInstalmentAmount: option.first_instalment_amount,
      firstRepaymentDate: option.first_repayment_date,
      frequency: option.frequency,
      itemsBreakdownLastRepayment: option.items_breakdown_last_repayment?.map(item => ({
        idItems: item.id_items,
        type: item.type,
      })),
      itemsBreakdownRepayment: option.items_breakdown_repayment?.map(item => ({
        idItems: item.id_items,
        type: item.type,
      })),
      lastRepaymentAmount: option.last_instalment_amount,
      lastRepaymentDate: option.last_repayment_date,
      numberOfRepayments: option.number_of_repayments,
      repaymentAmount: option.repayment_amount,
      serviceFee: option.service_fee,
      totalAmount: option.total_amount,
    })),
    initialPaymentSchedule: result.initial_payment_schedule ? {
      paymentType: result.initial_payment_schedule.payment_type,
      dueDate: result.initial_payment_schedule.due_date,
      baseAmount: result.initial_payment_schedule.base_amount,
      feeAmount: result.initial_payment_schedule.fee_amount,
      totalAmount: result.initial_payment_schedule.total_amount,
      fee: result.initial_payment_schedule.fee,
      creditApplied: result.initial_payment_schedule.credit_applied,
      expectedAmount: result.initial_payment_schedule.expected_amount,
    } : null,
  }
}

export function paymentScheduleDetailsMap(result: PaymentScheduleDetailsResponse['result']): App.PaymentScheduleDetails | undefined {
  if (!result) {
    return undefined
  }
  return mapPaymentSchedule(result)
}

type NonNullPaymentScheduleDetails = NonNullable<PaymentDetailsForOrdersResponse['result'][number]['payment_schedule_details']>

function mapPaymentSchedule(result: NonNullPaymentScheduleDetails): App.PaymentScheduleDetails {
  return {
    totalAmount: result.total_amount,
    totalPaid: result.total_paid,
    totalRefunded: result.total_refunded,
    balanceAmount: result.balance_amount,
    status: result.status,
    charges: result.payment_schedules.map((paymentSchedule) => ({
      id: paymentSchedule.id,
      paymentType: paymentSchedule.payment_type,
      dueDate: paymentSchedule.due_date,
      baseAmount: paymentSchedule.base_amount,
      feeAmount: paymentSchedule.fee_amount,
      totalAmount: paymentSchedule.total_amount,
      totalRefunded: paymentSchedule.total_refunded,
      status: paymentSchedule.status,
      amountType: paymentSchedule.amount_type,
      fee: paymentSchedule.fee,
      nextChargeDate: paymentSchedule.next_charge_date,
      items: paymentSchedule.items.map(item => ({
        itemId: item.id_items,
        type: item.type,
      })),
    })),
    scheduledItemsCount: result.scheduled_items.length,
    scheduleTemplateName: result.schedule_template_name,
    isMultiItem: result.is_multi_item ?? result.scheduled_items.length > 1,
  }
}

interface PaymentDetailsForOrder {
  payments: Array<App.OrderPayment>
  depositDetails?: App.Deposit
  instalmentDetails?: App.InstalmentDetails
  deferredPaymentDetails?: App.ReserveForZeroDetails
  paymentScheduleDetails?: App.PaymentScheduleDetails
  groupPaymentDetails?: App.GroupPaymentDetails
}

type GroupPaymentDetailsApi =
  PaymentDetailsForOrdersResponse['result'][number]['group_payment_details']

function mapGroupPaymentDetails(
  details: GroupPaymentDetailsApi,
): App.GroupPaymentDetails | undefined {
  if (!details) {
    return undefined
  }

  return {
    id: details.id,
    orderId: details.orderId,
    customerId: details.customerId,
    paymentScheduleId: details.paymentScheduleId,
    currency: details.currency,
    createdAt: details.createdAt,
    updatedAt: details.updatedAt,
    contributions: details.contributions.map(contribution => ({
      id: contribution.id,
      email: contribution.email,
      role: contribution.role,
      status: contribution.status,
      amount: contribution.amount,
      dueDate: contribution.dueDate,
      payable: contribution.payable,
      customerId: contribution.customerId,
      paymentScheduleChargeId: contribution.paymentScheduleChargeId,
      createdAt: contribution.createdAt,
      updatedAt: contribution.updatedAt,
    })),
  }
}

function mapInstalmentPaymentDetails(instalmentPaymentDetails: NonNullable<PaymentDetailsForOrdersResponse['result'][number]['instalment_details']>['instalment_payments'][number]): App.Instalment {
  return {
    id: instalmentPaymentDetails.id,
    instalments_number: instalmentPaymentDetails.instalments_number,
    instalment_amount: instalmentPaymentDetails.instalment_amount,
    instalment_due_date: instalmentPaymentDetails.instalment_due_date,
    instalment_paid_date: instalmentPaymentDetails.instalment_paid_date,
    currency: instalmentPaymentDetails.currency,
    instalment_status: instalmentPaymentDetails.instalment_status,
    due_balance_auto_debit_failed_count: instalmentPaymentDetails.due_balance_auto_debit_failed_count,
    created_at: instalmentPaymentDetails.created_at,
    metadata: {
      is_credit: instalmentPaymentDetails.metadata.is_credit,
      is_promo: instalmentPaymentDetails.metadata.is_promo,
    },
  }
}

function mapInstalmentDetails(instalmentDetails: PaymentDetailsForOrdersResponse['result'][number]['instalment_details']): App.InstalmentDetails | undefined {
  if (!instalmentDetails) {
    return undefined
  }

  return {
    instalment_status: instalmentDetails.instalment_status,
    total_paid_amount: instalmentDetails.total_paid_amount,
    balance_amount: instalmentDetails.balance_amount,
    currency: instalmentDetails.currency,
    total_amount: instalmentDetails.total_amount,
    fk_orders: instalmentDetails.fk_orders,
    instalment_frequency: instalmentDetails.instalment_frequency,
    total_instalments_count: instalmentDetails.total_instalments_count,
    instalment_frequency_days: Number(instalmentDetails.instalment_frequency_days),
    per_instalment_amount: instalmentDetails.per_instalment_amount,
    initial_payment_date: instalmentDetails.initial_payment_date,
    initial_payment_amount: instalmentDetails.initial_payment_amount,
    scheduled_instalment_dates: instalmentDetails.scheduled_instalment_dates,
    total_due_balance_auto_debit_failed_count: instalmentDetails.total_due_balance_auto_debit_failed_count,
    paid_instalments_count: instalmentDetails.paid_instalments_count,
    items: instalmentDetails.items,
    is_active: instalmentDetails.is_active,
    metadata: {
      external_ref: instalmentDetails.metadata.external_ref,
    },
    last_instalment_amount: instalmentDetails.last_instalment_amount,
    fk_customer: instalmentDetails.fk_customer,
    instalmentPaymentDetails: instalmentDetails.instalment_payments.map(i => mapInstalmentPaymentDetails(i)),
  }
}

function mapDepositDetails(depositDetails: PaymentDetailsForOrdersResponse['result'][number]['deposit_details']): App.Deposit | undefined {
  if (!depositDetails) {
    return undefined
  }
  return {
    deposit_status: depositDetails.deposit_status,
    paid_amount: depositDetails.paid_amount,
    currency: depositDetails.currency,
    balance_amount: depositDetails.balance_amount,
    balance_due_date: depositDetails.balance_due_date,
    balance_paid_date: depositDetails.balance_paid_date,
    fk_orders: depositDetails.fk_orders,
    deposit_metadata: depositDetails.deposit_metadata,
    due_balance_auto_debit_failed_count: depositDetails.due_balance_auto_debit_failed_count,
    total_amount: depositDetails.total_amount,
    createdAt: depositDetails.created_at,
    deposit_paid_date: depositDetails.deposit_paid_date,
    fk_customer: depositDetails.fk_customer,
    is_active: depositDetails.is_active,
  }
}

function mapDeferredPaymentDetails(deferredPaymentDetails: PaymentDetailsForOrdersResponse['result'][number]['deferred_payment_details']): App.ReserveForZeroDetails | undefined {
  if (!deferredPaymentDetails) {
    return undefined
  }
  return {
    orderIds: deferredPaymentDetails.fk_orders,
    payableAmount: deferredPaymentDetails.payable_amount,
    currency: deferredPaymentDetails.currency,
    isActive: deferredPaymentDetails.is_active,
    schedulePaymentDate: deferredPaymentDetails.scheduled_payment_date,
    bookingStatus: deferredPaymentDetails.booking_status,
    actualPaymentDate: deferredPaymentDetails.actual_payment_date,
  }
}

function mapPaymentScheduleDetails(paymentScheduleDetails: PaymentDetailsForOrdersResponse['result'][number]['payment_schedule_details']): App.PaymentScheduleDetails | undefined {
  if (!paymentScheduleDetails) {
    return undefined
  }
  return mapPaymentSchedule(paymentScheduleDetails)
}

export function getPaymentDetailsForOrdersMap(result: PaymentDetailsForOrdersResponse['result']): Record<string, PaymentDetailsForOrder> {
  return arrayToObject(result,
    (item) => item.order_id,
    (item) => ({
      payments: item.payments.map(payment => ({
        id: payment.id_payment,
        type: payment.type as any,
        currencyCode: payment.currency,
        amount: payment.amount,
        status: payment.status,
        createdAt: payment.created_at,
        intent: payment.intent,
        depositDetails: mapDepositDetails(item.deposit_details),
        instalmentDetails: mapInstalmentDetails(item.instalment_details),
        isDeferred: !!item.deferred_payment_details,
        transaction_key: payment.transaction_key,
        pspName: payment.psp_name,
        id_promo_code: payment.response_json?.id_promo,
        code_name: payment.response_json?.code_name,
        itemsBreakdown: payment.items_breakdown?.map(item => {
          const details = payment.type === CLASSIC_REWARD_PAYMENT_TYPE
            ? item.item_le_points_details
            : undefined
          return {
            itemId: item.id_items,
            totalAmount: item.amount,
            merchantFee: item.merchant_fee,
            type: item.type,
            lePointsDetails: details && isClassicRewardPointsDetails(details) ? {
              entryId: details.entry_id,
              reservationId: details.reservation_id,
              itemId: details.item_id,
              itemReference: details.item_reference,
              pointsPrice: details.points_price,
              cashPrice: details.cash_price,
              pointsValue: details.points_value,
              entryTotal: details.entry_total,
              quantity: details.quantity,
            } : undefined,
          }
        }),
      })),
      depositDetails: mapDepositDetails(item.deposit_details),
      instalmentDetails: mapInstalmentDetails(item.instalment_details),
      deferredPaymentDetails: mapDeferredPaymentDetails(item.deferred_payment_details),
      paymentScheduleDetails: mapPaymentScheduleDetails(item.payment_schedule_details),
      groupPaymentDetails: mapGroupPaymentDetails(item.group_payment_details),
    }))
}

export function createPaymentRequestMap(result: CreatePaymentRequestResponse['result']): PaymentRequest {
  if (result.paypal?.paymentRequestType === 'order') {
    return {
      paypal: {
        paymentRequestType: result.paypal.paymentRequestType,
        id: result.paypal.id,
      },
    }
  }

  if (result.paypal?.paymentRequestType === 'vault_setup') {
    return {
      paypal: {
        paymentRequestType: result.paypal.paymentRequestType,
        setupToken: result.paypal.setupToken,
      },
    }
  }

  return {}
}

export function adjustPaymentScheduleMap(result: AdjustPaymentScheduleResponse['result']): AdjustPaymentScheduleResponseData {
  return {
    eligible: result.eligible,
    adjustedCharges: result.adjusted_payment_schedule.charges.map(schedule => ({
      id: schedule.id,
      idRef: schedule.id_ref,
      chargeNumber: schedule.charge_number,
      chargeName: schedule.charge_name,
      chargeDate: schedule.charge_date,
      amountBase: schedule.amount_base,
      amountFee: schedule.amount_fee,
      items: schedule.items.map(item => ({
        idItems: item.id_items,
        type: item.type,
      })),
      initialTotalAmount: schedule.initial_total_amount,
      totalAmount: schedule.total_amount,
      totalPaid: schedule.total_paid,
      totalRefunded: schedule.total_refunded,
      balance: schedule.balance,
      status: schedule.status,
      transactionKey: schedule.transaction_key,
      source: schedule.source,
      action: schedule.action,
      paidDate: schedule.paid_date,
      nextChargeDate: schedule.next_charge_date,
      jobRetryAttempts: schedule.job_retry_attempts,
      jobFailureReason: schedule.job_failure_reason,
      adjustmentAmount: schedule.adjustment_amount,
      adjustmentDate: schedule.adjustment_date,
    })),
    adjustedSummary: {
      payNowAmount: result.adjusted_payment_schedule.summary.pay_now_amount,
      scheduleAmount: result.adjusted_payment_schedule.summary.schedule_amount,
      refundAmount: result.adjusted_payment_schedule.summary.refund_amount,
    },
    isDynamicTemplate: result.adjusted_payment_schedule.is_dynamic_template,
    frequency: result.adjusted_payment_schedule.frequency,
  }
}

export function getPayPalSettingsMap(result: PayPalSettings['result']): App.PayPalSettings {
  return {
    payLaterSettings: {
      regionSettings: result.payLaterSettings.regionSettings,
    },
  }
}

export function paymentTransactionDetailsMap(payments: Array<PaymentDetails>): Array<App.PaymentTransactionDetail> {
  return payments.map(payment => ({
    type: payment.type,
    status: payment.status,
    giftCardNumber: payment.gift_card_number,
    amount: payment.amount,
    currency: payment.currency,
  }))
}
